import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { ActivitySummary } from '../components/ActivitySummary.tsx';
import { GarminAiAttribution, RideAttribution } from '../components/GarminAttribution.tsx';
import { ProviderIcon } from '../components/ProviderIcon.tsx';
import { LucideIcon } from '../components/icons/LucideIcon.tsx';
import { providerLabel, toProvider } from '../lib/providers.ts';
import { StravaLink } from '../components/StravaLink.tsx';
import { PageShell } from '../components/PageShell.tsx';
import { BestEfforts } from '../components/charts/BestEfforts.tsx';
import { RideChart } from '../components/charts/RideChart.tsx';
import { skeletonKeys } from '../lib/skeletonKeys.ts';
import {
  useAnalyzeRide,
  useRefreshRideZones,
  useRide,
  useRideSamples,
} from '../hooks/useRides.ts';
import type {
  RideDetail as RideDetailType,
  RideLap,
  ZoneBucket,
} from '../types/api.ts';
import {
  formatDistanceKm,
  formatDuration,
  formatElevation,
  formatRideDate,
  formatTss,
  rideHasPower,
  rideTypeChip,
  tssEstimated,
} from '../lib/rideFormat.ts';
import { isActivity } from '../lib/sports.ts';

export function RideDetail() {
  const { id } = useParams();
  const rideId = id ? Number(id) : null;
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { data: ride, isLoading, isError } = useRide(rideId);

  return (
    <PageShell title={ride?.name ?? ' '}>
      <div className="flex flex-col gap-5">
        <button
          onClick={() => navigate(-1)}
          className="self-start text-sm text-(--color-text-secondary) hover:text-(--color-text-primary) flex items-center gap-1"
        >
          <span aria-hidden="true">{'←'}</span>
          {t('rideDetail.back')}
        </button>

        {isLoading && <DetailSkeleton />}

        {isError && (
          <div className="rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-3 text-sm">
            {t('rideDetail.errorLoading')}
          </div>
        )}

        {!isLoading && !isError && ride && (
          // A run, swim or gym session: summary only, and none of the
          // ride-only requests (samples, zones, analyze).
          isActivity(ride) ? <ActivitySummary item={ride} /> : <RideContent ride={ride} />
        )}

        <Link to="/rides" className="hidden">
          {t('rideDetail.back')}
        </Link>
      </div>
    </PageShell>
  );
}

function RideContent({ ride }: Readonly<{ ride: RideDetailType }>) {
  const { t } = useTranslation();
  const { data: samplesData } = useRideSamples(ride.id, ride.has_samples);
  const analyzeMut = useAnalyzeRide();
  const [analyzeError, setAnalyzeError] = useState('');

  const handleAnalyze = async () => {
    setAnalyzeError('');
    try {
      await analyzeMut.mutateAsync(ride.id);
    } catch (err) {
      const msg =
        err instanceof Error ? err.message : t('rideDetail.analyzeFailed');
      setAnalyzeError(msg);
    }
  };
  useAnalyzeParam(ride.coach_summary == null, handleAnalyze);

  return (
    <>
      <RideAttribution text={ride.attribution} className="-mt-3" />
      <RideHeader ride={ride} />
      <StatsGrid ride={ride} />
      <BestEfforts rideId={ride.id} bests={ride.power_bests} />
      <ZoneBars ride={ride} />
      <CoachSummary
        summary={analyzeMut.data?.summary ?? ride.coach_summary}
        summaryAt={analyzeMut.data?.generated_at ?? ride.coach_summary_at}
        sources={analyzeMut.data?.ai_sources ?? ride.coach_summary_sources}
        isPending={analyzeMut.isPending}
        error={analyzeError}
        onAnalyze={handleAnalyze}
      />
      <RideChart samples={samplesData?.samples ?? []} hasSamples={ride.has_samples} />
      {ride.laps.length > 0 && <LapsTable laps={ride.laps} />}
      <StravaLink ride={ride} />
    </>
  );
}

function CoachSummary({
  summary,
  summaryAt,
  isPending,
  error,
  onAnalyze,
  sources,
}: Readonly<{
  summary: string | null;
  summaryAt: string | null;
  sources?: string[];
  isPending: boolean;
  error: string;
  onAnalyze: () => void;
}>) {
  const { t } = useTranslation();
  const analyzeLabel = summary
    ? `🤖 ${t('rideDetail.regenerate')}`
    : `🤖 ${t('rideDetail.analyzeRide')}`;
  return (
    <div className="rounded-lg bg-(--color-bg-secondary) border border-(--color-border) p-4 flex flex-col gap-2">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h3 className="text-sm font-medium text-(--color-text-secondary)">
          {t('rideDetail.coachSummaryTitle')}
        </h3>
        <button
          onClick={onAnalyze}
          disabled={isPending}
          className="px-3 py-1 rounded-lg bg-(--color-accent)/15 text-(--color-accent) hover:bg-(--color-accent)/25 transition-colors text-xs font-medium border border-(--color-accent)/30 disabled:opacity-50"
        >
          {isPending ? t('rideDetail.analyzing') : analyzeLabel}
        </button>
      </div>
      {error && (
        <div className="rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 px-3 py-2 text-xs">
          {error}
        </div>
      )}
      {summary ? (
        <div className="rounded-lg bg-(--color-bg-tertiary) border border-(--color-border) px-3 py-3 text-sm text-(--color-text-primary) whitespace-pre-wrap leading-relaxed">
          {summary}
          <GarminAiAttribution sources={sources} className="mt-2" />
        </div>
      ) : (
        <p className="text-xs text-(--color-text-secondary)">
          {t('rideDetail.noSummaryYet')}
        </p>
      )}
      {summaryAt && (
        <p className="text-[11px] text-(--color-text-secondary)">
          {t('rideDetail.generatedAt', {
            when: new Date(summaryAt).toLocaleString(),
          })}
        </p>
      )}
    </div>
  );
}

function RideHeader({ ride }: Readonly<{ ride: RideDetailType }>) {
  const { t } = useTranslation();
  const chip = rideTypeChip(ride.ride_type);
  const provider = toProvider(ride.provider);
  return (
    <div className="flex flex-wrap items-center gap-3">
      <LucideIcon name={ride.is_indoor ? 'house' : 'bike'} size={22} className="text-(--color-text-secondary)" />
      <div className="text-(--color-text-secondary) text-sm">
        {formatRideDate(ride.started_at)}
      </div>
      <span
        className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-medium border ${chip.className}`}
      >
        {chip.label}
      </span>
      {ride.device_name && !ride.attribution && (
        <span className="text-xs text-(--color-text-secondary)">
          {ride.device_name}
        </span>
      )}
      <span className="inline-flex items-center gap-1.5 text-xs text-(--color-text-secondary)" data-testid="ride-provider">
        <ProviderIcon provider={provider} size={14} decorative />
        {t('rideDetail.fromSource', { source: providerLabel(provider, t) })}
      </span>
    </div>
  );
}

function powerStats(ride: RideDetailType, t: TFunction): { label: string; value: string }[] {
  return [
    { label: t('rideDetail.stats.avgPower'), value: ride.avg_power == null ? '—' : `${ride.avg_power} W` },
    { label: t('rideDetail.stats.normalizedPower'), value: ride.normalized_power == null ? '—' : `${ride.normalized_power} W` },
    { label: t('rideDetail.stats.intensityFactor'), value: ride.intensity_factor == null ? '—' : ride.intensity_factor.toFixed(2) },
  ];
}

function workStats(ride: RideDetailType, t: TFunction): { label: string; value: string }[] {
  return [
    {
      label: t('rideDetail.stats.totalWork'),
      value: ride.total_work_kj == null ? '—' : `${Math.round(ride.total_work_kj)} kJ`,
    },
    {
      label: t('rideDetail.stats.ftpAtTime'),
      value: ride.ftp_at_time == null ? '—' : `${ride.ftp_at_time} W`,
    },
  ];
}

function StatsGrid({ ride }: Readonly<{ ride: RideDetailType }>) {
  const { t } = useTranslation();
  // No power (e.g. a ride from Apple Health): no power stats, and the TSS
  // is a heart-rate estimate.
  const power = rideHasPower(ride);
  const stats: { label: string; value: string }[] = [
    { label: t('rideDetail.stats.duration'), value: formatDuration(ride.duration_sec) },
    { label: t('rideDetail.stats.distance'), value: formatDistanceKm(ride.distance_m) },
    ...(power ? powerStats(ride, t) : []),
    {
      label: tssEstimated(ride) ? t('rideDetail.stats.tssFromHr') : t('rideDetail.stats.tss'),
      value: formatTss(ride),
    },
    { label: t('rideDetail.stats.avgHr'), value: ride.avg_hr == null ? '—' : `${ride.avg_hr} bpm` },
    { label: t('rideDetail.stats.maxHr'), value: ride.max_hr == null ? '—' : `${ride.max_hr} bpm` },
    { label: t('rideDetail.stats.avgCadence'), value: ride.avg_cadence == null ? '—' : `${Math.round(ride.avg_cadence)} rpm` },
    { label: t('rideDetail.stats.calories'), value: ride.calories == null ? '—' : `${ride.calories}` },
    { label: t('rideDetail.stats.elevation'), value: formatElevation(ride.elevation_gain_m) },
    ...(power ? workStats(ride, t) : []),
  ];

  return (
    <div className="flex flex-col gap-2">
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
        {stats.map((s) => (
          <div
            key={s.label}
            className="rounded-lg bg-(--color-bg-tertiary) border border-(--color-border) p-3 text-center"
          >
            <div className="text-xs text-(--color-text-secondary) mb-1">
              {s.label}
            </div>
            <div className="text-base font-semibold text-(--color-text-primary)">
              {s.value}
            </div>
          </div>
        ))}
      </div>
      {tssEstimated(ride) && (
        <p className="text-xs text-(--color-text-secondary)">{t('rideDetail.tssFromHrNote')}</p>
      )}
    </div>
  );
}

const HR_ZONE_COLORS = [
  'bg-blue-500',
  'bg-green-500',
  'bg-yellow-500',
  'bg-orange-500',
  'bg-red-500',
];

const POWER_ZONE_COLORS = [
  'bg-gray-500',
  'bg-blue-500',
  'bg-green-500',
  'bg-yellow-500',
  'bg-orange-500',
  'bg-red-500',
  'bg-fuchsia-500',
];

function ZoneBars({ ride }: Readonly<{ ride: RideDetailType }>) {
  const { t } = useTranslation();
  const refreshMut = useRefreshRideZones();
  const [refreshError, setRefreshError] = useState('');

  const hr = refreshMut.data
    ? (refreshMut.data.hr_buckets as ZoneBucket[] | null)
    : ride.hr_zone_buckets;
  const power = refreshMut.data
    ? (refreshMut.data.power_buckets as ZoneBucket[] | null)
    : ride.power_zone_buckets;

  const hrTotal = useMemo(() => sumTime(hr), [hr]);
  const powerTotal = useMemo(() => sumTime(power), [power]);

  const handleRefresh = async () => {
    setRefreshError('');
    try {
      await refreshMut.mutateAsync(ride.id);
    } catch (err) {
      const msg =
        err instanceof Error ? err.message : t('rideDetail.zones.refreshFailed');
      setRefreshError(msg);
    }
  };

  if (!hr && !power) {
    return (
      <div className="rounded-lg bg-(--color-bg-secondary) border border-(--color-border) px-4 py-3 flex items-center justify-between gap-2 flex-wrap">
        <div>
          <h3 className="text-sm font-medium text-(--color-text-secondary)">
            {t('rideDetail.zones.title')}
          </h3>
          <p className="text-xs text-(--color-text-secondary) mt-1">
            {t('rideDetail.zones.notFetched')}
          </p>
        </div>
        {ride.source === 'strava' && (
          <button
            onClick={handleRefresh}
            disabled={refreshMut.isPending}
            className="px-3 py-1.5 rounded-lg bg-(--color-accent)/15 text-(--color-accent) hover:bg-(--color-accent)/25 transition-colors text-xs font-medium border border-(--color-accent)/30 disabled:opacity-50"
          >
            {refreshMut.isPending
              ? t('rideDetail.zones.refreshing')
              : t('rideDetail.zones.fetch')}
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="rounded-lg bg-(--color-bg-secondary) border border-(--color-border) p-4 flex flex-col gap-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h3 className="text-sm font-medium text-(--color-text-secondary)">
          {t('rideDetail.zones.title')}
        </h3>
        {ride.source === 'strava' && (
          <button
            onClick={handleRefresh}
            disabled={refreshMut.isPending}
            className="px-2.5 py-1 rounded-lg text-xs text-(--color-text-secondary) hover:text-(--color-text-primary) hover:bg-(--color-bg-tertiary) transition-colors border border-(--color-border) disabled:opacity-50"
          >
            {refreshMut.isPending
              ? t('rideDetail.zones.refreshing')
              : t('rideDetail.zones.refresh')}
          </button>
        )}
      </div>
      {refreshError && (
        <div className="rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 px-3 py-2 text-xs">
          {refreshError}
        </div>
      )}
      {hr && hrTotal > 0 && (
        <ZoneSection
          label={t('rideDetail.zones.hr')}
          buckets={hr}
          total={hrTotal}
          colors={HR_ZONE_COLORS}
          unit="bpm"
        />
      )}
      {power && powerTotal > 0 && (
        <ZoneSection
          label={t('rideDetail.zones.power')}
          buckets={power}
          total={powerTotal}
          colors={POWER_ZONE_COLORS}
          unit="W"
        />
      )}
    </div>
  );
}

function ZoneSection({
  label,
  buckets,
  total,
  colors,
  unit,
}: Readonly<{
  label: string;
  buckets: ZoneBucket[];
  total: number;
  colors: string[];
  unit: string;
}>) {
  return (
    <div>
      <div className="text-xs text-(--color-text-secondary) mb-1.5">{label}</div>
      <div className="flex h-6 rounded-md overflow-hidden border border-(--color-border)">
        {buckets.map((b, i) => {
          const pct = total > 0 ? (b.time / total) * 100 : 0;
          if (pct < 0.5) return null;
          return (
            <div
              key={`${b.min}-${b.max}`}
              style={{ width: `${pct}%` }}
              className={`${colors[i % colors.length]} flex items-center justify-center text-[10px] font-bold text-white`}
              title={`Z${i + 1} ${formatRange(b, unit)}: ${formatBucketTime(b.time)} (${pct.toFixed(1)}%)`}
            >
              {pct >= 6 ? `${pct.toFixed(0)}%` : ''}
            </div>
          );
        })}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-3 gap-y-0.5 text-[11px] text-(--color-text-secondary) mt-2">
        {buckets.map((b, i) => (
          <div key={`${b.min}-${b.max}`} className="flex items-center gap-1.5">
            <span
              className={`inline-block w-2.5 h-2.5 rounded-sm ${colors[i % colors.length]}`}
            />
            <span className="text-(--color-text-primary)">Z{i + 1}</span>
            <span>{formatRange(b, unit)}</span>
            <span className="ml-auto">{formatBucketTime(b.time)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function sumTime(buckets: ZoneBucket[] | null): number {
  if (!buckets) return 0;
  let total = 0;
  for (const b of buckets) total += b.time ?? 0;
  return total;
}

function formatRange(b: ZoneBucket, unit: string): string {
  // Strava uses -1 to mean "no upper limit" on the last bucket.
  const lo = b.min;
  const hi = b.max;
  if (hi === -1 || hi == null) return `${lo}+${unit}`;
  return `${lo}-${hi}${unit}`;
}

function formatBucketTime(seconds: number): string {
  if (!seconds) return '0';
  const min = Math.round(seconds / 60);
  if (min < 60) return `${min}m`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return `${h}h${m.toString().padStart(2, '0')}m`;
}


function LapsTable({ laps }: Readonly<{ laps: RideLap[] }>) {
  const { t } = useTranslation();
  return (
    <div className="rounded-lg bg-(--color-bg-secondary) border border-(--color-border) overflow-hidden">
      <h3 className="text-sm font-medium text-(--color-text-secondary) px-4 py-2 border-b border-(--color-border)">
        {t('rideDetail.laps.title')}
      </h3>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-xs text-(--color-text-secondary) bg-(--color-bg-tertiary)">
            <tr>
              <th className="px-3 py-2 text-left">#</th>
              <th className="px-3 py-2 text-right">{t('rideDetail.laps.duration')}</th>
              <th className="px-3 py-2 text-right">{t('rideDetail.laps.distance')}</th>
              <th className="px-3 py-2 text-right">{t('rideDetail.laps.avgPower')}</th>
              <th className="px-3 py-2 text-right">{t('rideDetail.laps.avgHr')}</th>
            </tr>
          </thead>
          <tbody>
            {laps.map((lap) => (
              <tr key={lap.lap_index} className="border-t border-(--color-border)/60">
                <td className="px-3 py-2 text-(--color-text-primary)">
                  {lap.name ?? `${t('rideDetail.laps.lap')} ${lap.lap_index + 1}`}
                </td>
                <td className="px-3 py-2 text-right text-(--color-text-primary)">
                  {formatDuration(lap.duration_sec)}
                </td>
                <td className="px-3 py-2 text-right text-(--color-text-primary)">
                  {formatDistanceKm(lap.distance_m)}
                </td>
                <td className="px-3 py-2 text-right text-(--color-text-primary)">
                  {lap.avg_power == null ? '—' : `${lap.avg_power} W`}
                </td>
                <td className="px-3 py-2 text-right text-(--color-text-primary)">
                  {lap.avg_hr == null ? '—' : `${lap.avg_hr} bpm`}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const DETAIL_SKELETON_KEYS = skeletonKeys(8);

function DetailSkeleton() {
  return (
    <div className="flex flex-col gap-4 animate-pulse">
      <div className="h-6 w-48 rounded bg-(--color-bg-secondary)" />
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {DETAIL_SKELETON_KEYS.map((key) => (
          <div
            key={key}
            className="h-16 rounded-lg bg-(--color-bg-secondary) border border-(--color-border)"
          />
        ))}
      </div>
      <div className="h-64 rounded-lg bg-(--color-bg-secondary) border border-(--color-border)" />
    </div>
  );
}

/** ``?analyze=1`` (the ride's quick action): start the analysis once when
 * the ride has none, then drop the param. The usual Pro gate applies. */
function useAnalyzeParam(needed: boolean, analyze: () => Promise<void>) {
  const [params, setParams] = useSearchParams();
  const started = useRef(false);
  const asked = params.get('analyze') === '1';
  useEffect(() => {
    if (!asked || started.current) return;
    started.current = true;
    setParams({}, { replace: true });
    if (needed) void analyze();
  }, [asked, needed, analyze, setParams]);
}
